<template>
  <div class="upload-view">
    <div class="upload-header">
      <h2>上传图片</h2>
      <p class="upload-tip">支持jpg、png格式，单张图片不超过5MB</p>
    </div>

    <div class="upload-area" @drop.prevent="handleDrop" @dragover.prevent>
      <input
        type="file"
        ref="fileInput"
        multiple
        accept="image/jpeg,image/png"
        @change="handleFileSelect"
        class="file-input"
      />
      <div class="upload-placeholder" @click="triggerFileInput">
        <i class="fas fa-cloud-upload-alt"></i>
        <p>点击或拖拽图片到此处上传</p>
      </div>
    </div>

    <div class="images-list" v-if="uploadImages.length">
      <div
        v-for="(image, index) in uploadImages"
        :key="index"
        class="image-item"
      >
        <div class="image-preview">
          <img :src="image.preview" :alt="image.name" />
          <button class="remove-btn" @click="removeImage(index)">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="image-info">
          <div class="form-group">
            <label>图片名称</label>
            <input
              type="text"
              v-model="image.customName"
              placeholder="请输入图片名称"
            />
          </div>
          <div class="form-group">
            <label>标签</label>
            <div class="tags-input">
              <div class="tags-container">
                <span
                  v-for="(tag, tagIndex) in image.tags"
                  :key="tagIndex"
                  class="tag"
                >
                  {{ tag }}
                  <i
                    class="fas fa-times"
                    @click="removeTag(index, tagIndex)"
                  ></i>
                </span>
              </div>
              <input
                type="text"
                v-model="image.currentTag"
                @keyup.enter="addTag(index)"
                @keyup="handleTagInput(index, $event)"
                placeholder="输入标签后按回车或逗号添加"
              />
              <div class="suggested-tags">
                <span>建议标签：</span>
                <div class="tag-suggestions">
                  <span
                    v-for="tag in suggestedTags"
                    :key="tag"
                    class="suggested-tag"
                    @click="addSuggestedTag(index, tag)"
                    :class="{ 'is-selected': image.tags.includes(tag) }"
                  >
                    {{ tag }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="upload-actions" v-if="uploadImages.length">
      <button class="upload-btn" @click="handleUpload" :disabled="uploading">
        <i class="fas fa-upload"></i>
        {{ uploading ? "上传中..." : "开始上传" }}
      </button>
    </div>
  </div>
</template>

<script>
import { request } from "@/utils/request";
// 移除静态标签导入
// import { simpleTags } from '@/config/tags'

export default {
  name: "UploadView",
  data() {
    return {
      uploadImages: [],
      uploading: false,
      suggestedTags: [] // 改为空数组，从服务端获取
    };
  },
  // 添加 created 生命周期钩子
  async created() {
    await this.loadTags();
  },
  methods: {
    // 新增标签获取方法
    async loadTags() {
      try {
        const data = await request('/api/tag/list')
        this.suggestedTags = data.data
          .sort((a, b) => a.sort - b.sort)
          .map(item => item.name) // 只需要标签名称
      } catch (error) {
        console.error('获取标签失败:', error)
        // 如果获取失败，可以设置一些默认标签
        this.suggestedTags = ['风景', '人物', '动物', '建筑', '美食']
      }
    },
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    handleFileSelect(event) {
      this.addFiles(Array.from(event.target.files));
      event.target.value = ""; // 清空input，允许重复选择相同文件
    },
    handleDrop(event) {
      this.addFiles(
        Array.from(event.dataTransfer.files).filter((file) => {
          return file.type === "image/jpeg" || file.type === "image/png";
        })
      );
    },
    addFiles(files) {
      files.forEach((file) => {
        if (file.size > 5 * 1024 * 1024) {
          alert("图片大小不能超过5MB");
          return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
          this.uploadImages.push({
            file,
            preview: e.target.result,
            customName: file.name.split(".")[0], // 默认使用文件名
            tags: [],
            currentTag: "",
          });
        };
        reader.readAsDataURL(file);
      });
    },
    removeImage(index) {
      this.uploadImages.splice(index, 1);
    },
    handleTagInput(imageIndex, event) {
      if (event.key === ",") {
        event.preventDefault();
        this.addTag(imageIndex);
      }
    },
    addTag(imageIndex) {
      const image = this.uploadImages[imageIndex];
      const tag = image.currentTag.trim().replace(/,/g, "");
      if (tag && !image.tags.includes(tag)) {
        image.tags.push(tag);
      }
      image.currentTag = "";
    },
    removeTag(imageIndex, tagIndex) {
      this.uploadImages[imageIndex].tags.splice(tagIndex, 1);
    },
    addSuggestedTag(imageIndex, tag) {
      const image = this.uploadImages[imageIndex];
      if (!image.tags.includes(tag)) {
        image.tags.push(tag);
      } else {
        // 如果标签已存在，则移除它
        const tagIndex = image.tags.indexOf(tag);
        image.tags.splice(tagIndex, 1);
      }
    },
    async handleUpload() {
      if (this.uploading) return; // 如果正在上传，直接返回
      this.uploading = true;

      try {
        const formData = new FormData();

        // 1. 添加 files 部分（List<MultipartFile>）
        this.uploadImages.forEach((image) => {
          formData.append(`files`, image.file); // 注意：字段名是 files
        });

        let images = [];
        this.uploadImages.forEach((image) => {
          images.push({
            imageName: image.customName,
            tags: image.tags
          })
        });

        formData.set('images', new Blob([JSON.stringify(images)], { type: 'application/json' }));

        // // 2. 添加 images 部分（List<ApiImageUploadForm>）
        // this.uploadImages.forEach((image, index) => {
        //   // 添加文件
        //   formData.append(`images[${index}].file`, image.file);

        //   // 添加图片名称
        //   formData.append(`images[${index}].imageName`, image.customName);

        //   // 添加标签（如果是数组）
        //   if (Array.isArray(image.tags)) {
        //     image.tags.forEach((tag) => {
        //       formData.append(`images[${index}].tags`, tag);
        //     });
        //   }
        // });

        // 调试 FormData
        for (let [key, value] of formData.entries()) {
          console.log(key, value);
        }

        // 使用封装的 request 方法发送请求
        const response = await request("/api/image/upload", {
          method: "POST",
          body: formData,
          // 不要手动设置 Content-Type，浏览器会自动处理
        });

        // 检查响应状态
        if (!response.ok) {
          throw new Error(response.msg || "上传失败");
        }

        // 上传成功后跳转到图片列表页
        this.$router.push("/photos");
      } catch (error) {
        console.error("上传失败:", error);
        alert(error.message || "上传失败，请重试");
      } finally {
        this.uploading = false; // 重置上传状态
      }
    },
  },
};
</script>

<style scoped>
.upload-view {
  max-width: 1200px;
  margin: 40px auto;
  padding: 0 20px;
}

.upload-header {
  text-align: center;
  margin-bottom: 30px;
}

.upload-tip {
  color: #666;
  margin-top: 8px;
}

.upload-area {
  border: 2px dashed #ddd;
  border-radius: 8px;
  padding: 40px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 30px;
}

.upload-area:hover {
  border-color: #4caf50;
}

.file-input {
  display: none;
}

.upload-placeholder {
  color: #666;
}

.upload-placeholder i {
  font-size: 48px;
  color: #4caf50;
  margin-bottom: 16px;
}

.images-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 24px;
  margin-bottom: 30px;
}

.image-item {
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.image-preview {
  position: relative;
  padding-top: 75%;
}

.image-preview img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.remove-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  cursor: pointer;
  color: #666;
  transition: all 0.3s ease;
}

.remove-btn:hover {
  background: #f44336;
  color: white;
}

.image-info {
  padding: 16px;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  color: #333;
  font-weight: 500;
}

.form-group input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.tags-input {
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 12px;
}

.tags-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  background: #f5f5f5;
  border-radius: 16px;
  font-size: 14px;
}

.tag i {
  cursor: pointer;
  color: #666;
  font-size: 12px;
}

.tag i:hover {
  color: #f44336;
}

.suggested-tags {
  margin-top: 12px;
  font-size: 14px;
  color: #666;
}

.tag-suggestions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.suggested-tag {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  background: #f5f5f5;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  user-select: none;
}

.suggested-tag:hover {
  background: #e0e0e0;
}

.suggested-tag.is-selected {
  background: #4caf50;
  color: white;
}

.upload-actions {
  text-align: center;
}

.upload-btn {
  padding: 12px 32px;
  background: #4caf50;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.upload-btn:hover {
  background: #43a047;
}

.upload-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .upload-view {
    margin: 20px auto;
  }

  .images-list {
    grid-template-columns: 1fr;
  }

  .tag-suggestions {
    gap: 6px;
  }

  .suggested-tag {
    padding: 3px 10px;
    font-size: 13px;
  }
}
</style>
